import { useCallback, useEffect, useState } from 'react'
import { useI18n, formatar } from '../lib/i18n'

/**
 * Diplomas.
 *
 * Segue docs/07-gamificacao.md: o hobby JÁ é gamificado há décadas — DXCC, WAC,
 * QRP existem no mundo real. O trabalho aqui é digitalizar o que existe, não
 * inventar mecânica nova.
 *
 * A decisão de layout que importa: o que aparece em destaque é O QUE FALTA, não
 * a porcentagem. "Faltam OC e AN" manda o operador procurar Oceania na banda;
 * "67%" não muda o comportamento de ninguém.
 */

interface Award {
  id: string
  name: string
  detail: string
  have: number
  tier?: string
  next_tier?: string
  next_target?: number
  fraction: number
  complete: boolean
  missing?: string[]
  worked?: string[]
}

interface AwardsResponse {
  awards: Award[] | null
  iniciados: number
  completos: number
  total: number
}

export function Awards({ refreshKey }: { refreshKey?: number }) {
  const { t } = useI18n()
  const [awards, setAwards] = useState<Award[]>([])
  const [summary, setSummary] = useState<{ iniciados: number; total: number } | null>(null)
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)

  const load = useCallback(() => {
    fetch('/api/awards')
      .then((r) => r.json())
      .then((d: AwardsResponse) => {
        setAwards(d.awards ?? [])
        setSummary({ iniciados: d.iniciados, total: d.total })
      })
      .catch(() => {})
  }, [])

  useEffect(load, [load, refreshKey])

  // O diploma MAIS PERTO do próximo degrau vira o teaser do cabeçalho — assim
  // o progresso que já existe (next_target, have) aparece mesmo com o
  // acordeão fechado, em vez de só depois de abrir e procurar.
  const maisProximo = awards
    .filter((a) => !a.complete && a.next_target)
    .reduce<Award | null>((melhor, a) => {
      const falta = a.next_target! - a.have
      const faltaMelhor = melhor ? melhor.next_target! - melhor.have : Infinity
      return falta < faltaMelhor ? a : melhor
    }, null)

  return (
    <section className="awards">
      <button className="awards-toggle" onClick={() => setOpen((v) => !v)}>
        {t.awards.titulo}
        {summary && (
          <small>
            {formatar(t.awards.resumoIniciados, { iniciados: summary.iniciados, total: summary.total })}
            {maisProximo &&
              ` · ${maisProximo.name}: ${formatar(t.awards.faltamContagem, { n: maisProximo.next_target! - maisProximo.have })}`}
          </small>
        )}
      </button>

      {open && (
        <>
          <ul className="award-list">
            {awards.map((a) => (
              <li
                key={a.id}
                className={a.complete ? 'award done' : 'award'}
                onClick={() => setExpanded(expanded === a.id ? null : a.id)}
              >
                <div className="award-head">
                  <strong>{a.name}</strong>
                  {a.tier && <span className="pill on">{a.tier}</span>}
                  <span className="grow" />
                  <span className="award-count">
                    {a.have}
                    {a.next_target ? ` / ${a.next_target}` : ''}
                  </span>
                </div>

                <div className="bar">
                  <i style={{ width: `${Math.round(a.fraction * 100)}%` }} />
                </div>

                {/* O que falta vem antes do que já foi feito: é o que gera ação. */}
                {a.missing && a.missing.length > 0 && (
                  <p className="award-missing">
                    {formatar(t.awards.faltamLista, {
                      lista: a.missing.slice(0, expanded === a.id ? 99 : 4).join(', '),
                    })}
                    {expanded !== a.id && a.missing.length > 4 &&
                      ` ${formatar(t.awards.eMaisContagem, { n: a.missing.length - 4 })}`}
                  </p>
                )}

                {a.complete ? (
                  <p className="award-next">{t.awards.completo}</p>
                ) : (
                  a.next_tier && (
                    <p className="award-next">
                      {formatar(t.awards.proximoTier, { tier: a.next_tier, n: a.next_target! - a.have })}
                    </p>
                  )
                )}

                {expanded === a.id && (
                  <>
                    <p className="award-detail">{a.detail}</p>
                    {a.worked && a.worked.length > 0 && (
                      <p className="award-worked">{formatar(t.awards.feitosLista, { lista: a.worked.join(', ') })}</p>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>

          <p className="award-note">{t.awards.nota}</p>
        </>
      )}
    </section>
  )
}
